import { useCallback, useEffect, useRef, useState } from 'react';
import apiClient from '../../../utils/apiClient';
import { getCurrentUser } from '../../../utils/auth';

const POLL_EVERY_MS = 2000;
const POLL_ATTEMPTS = 30;
const TOO_SLOW = 'Your report is taking longer than usual. Check back in a few minutes.';

/** The student's latest AI report, and generating a new one (which finishes later). */
export default function useAiReport() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const poll = useRef(null);
  const studentId = getCurrentUser()?.studentId;

  const stopPolling = useCallback(() => {
    clearInterval(poll.current);
    poll.current = null;
  }, []);

  useEffect(() => stopPolling, [stopPolling]);

  useEffect(() => {
    if (!studentId) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    apiClient.get(`/ai-report/latest/${studentId}`)
      .then(({ data }) => { if (!cancelled) setReport(data?.report || null); })
      .catch(() => { if (!cancelled) setReport(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [studentId]);

  const generate = useCallback(async () => {
    if (!studentId || poll.current) return;
    setGenerating(true);
    try {
      await apiClient.post('/ai-report/generate', { studentId });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to generate report.');
      if (err.response?.data?.report) setReport(err.response.data.report);
      setGenerating(false);
      return;
    }

    let attempts = 0;
    poll.current = setInterval(async () => {
      attempts += 1;
      const outOfTime = attempts >= POLL_ATTEMPTS;
      if (outOfTime) stopPolling();
      try {
        const { data } = await apiClient.get(`/ai-report/latest/${studentId}`);
        if (data?.report?.overallScore > 0) {
          stopPolling();
          setReport(data.report);
          setGenerating(false);
          return;
        }
      } catch {
        // A failed read is not a failed report: the next tick asks again.
      }
      if (outOfTime) {
        setGenerating(false);
        setError(TOO_SLOW);
      }
    }, POLL_EVERY_MS);
  }, [studentId, stopPolling]);

  return {
    report, loading, generating, error, generate, dismissError: useCallback(() => setError(''), []),
  };
}
